import type { TargetCollaborationsSortColumn } from "@unbox/api";
import { Folder, Plus, SearchMd, Target03, TrendUp01, Users01 } from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import {
  useTargetCollaborationsCount,
  useTargetCollaborationsList,
  useTargetCollaborationsMetrics,
} from "@/api/collaborations";
import { usePagination } from "@/api/pagination";
import { useTikTokShopConnectionStatus } from "@/api/tiktok-shop";
import { useFreshenCollaborationList } from "@/api/use-freshen";
import { useDismissHero } from "@/api/user-preferences";
import { PageHero, PageHeroKey } from "@/components/application/page-hero/page-hero";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { FilterPanelLegacy as FilterPanel } from "@/components/filters/filter-panel-legacy";
import { LoadingPageState } from "@/components/loading-page-state";
import { PageLayout } from "@/components/page-layout";
import { useCache } from "@/hooks/use-cache";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { useSort } from "@/hooks/use-sort";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import { CollabsListEmptyState } from "./collabs-list-empty-state";
import { CreateTargetCollaborationModal } from "./create-target-collaboration-modal";
import { GroupExpansionController, type GroupExpansionState } from "./group-expansion-controller";
import { TargetCollaborationsTable } from "./target-collaborations-table";

const TargetCollaborationsPageContent = () => {
  const [search, setSearch] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { value: debouncedSearch, clear: clearSearch } = useClearableDebounce(search);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupExpansions, setGroupExpansions] = useState<Map<string, GroupExpansionState>>(new Map());

  const updateGroupExpansion = useCallback((groupId: string, state: GroupExpansionState | null) => {
    setGroupExpansions((prev) => {
      if (state === null) {
        if (!prev.has(groupId)) return prev;
        const next = new Map(prev);
        next.delete(groupId);
        return next;
      }
      const next = new Map(prev);
      next.set(groupId, state);
      return next;
    });
  }, []);

  const pagination = usePagination();
  const { sort, setFromDescriptor, descriptor } = useSort<TargetCollaborationsSortColumn>("updatedAt");

  const { reset } = useFilters();
  const dismissHero = useDismissHero();

  useFreshenCollaborationList();

  const { data: targetData, isLoading: isLoadingTarget } = useTargetCollaborationsList({
    search: debouncedSearch,
    pagination: pagination.pagination,
    sort: sort ? { column: sort.column, direction: sort.direction } : undefined,
  });
  const { data: targetCount } = useTargetCollaborationsCount(debouncedSearch);

  const metricRows = useMemo(() => {
    const rows: Array<{ key: string; collaborationIds: string[] }> = [];
    for (const c of targetData?.collaborations ?? []) {
      rows.push({
        key: c.groupId && c.isGroupHeader ? `group-${c.groupId}` : c.collaborationId,
        collaborationIds: c.groupCollaborationIds.length > 0 ? c.groupCollaborationIds : [c.collaborationId],
      });
    }
    for (const expansion of groupExpansions.values()) {
      for (const member of expansion.items) {
        rows.push({ key: member.collaborationId, collaborationIds: [member.collaborationId] });
      }
    }
    return rows;
  }, [targetData?.collaborations, groupExpansions]);
  const { data: targetMetrics } = useTargetCollaborationsMetrics(metricRows);

  const handleToggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const cachedTargetCount = useCache(targetCount);
  const loadedTargetTotal = useCache(targetData?.total);
  const { data: shopStatus } = useTikTokShopConnectionStatus();

  const resetFilters = () => {
    setSearch("");
    clearSearch();
    reset();
    pagination.setPage(1);
    setExpandedGroups(new Set());
  };

  // Genuine zero-data — or no Shop Access — replaces the whole page with the
  // full-bleed first-run empty state. A search keeps the inline table state.
  const shopUnauthorized = shopStatus ? !shopStatus.connected : false;
  if (!debouncedSearch && (shopUnauthorized || (!isLoadingTarget && (targetData?.total ?? 0) === 0))) {
    return (
      <>
        <CollabsListEmptyState variant="target" onCreate={() => setCreateModalOpen(true)} />
        <CreateTargetCollaborationModal
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          onCreated={() => dismissHero.mutate(PageHeroKey.TargetCollabs)}
        />
      </>
    );
  }

  if (!debouncedSearch && isLoadingTarget && loadedTargetTotal === undefined) return <LoadingPageState />;

  return (
    <PageLayout
      title="Target Collaborations"
      subtitle="Invite-only. You choose the creators, and you can set a custom commission rate and offer free samples."
      icon={Target03}
      callToAction={
        <Button
          color="primary"
          size="sm"
          iconLeading={<Plus className="w-4 h-4" />}
          onClick={() => setCreateModalOpen(true)}
        >
          Create Collaboration
        </Button>
      }
    >
      <PageHero
        pageKey={PageHeroKey.TargetCollabs}
        title="Follow every collaboration from sample to sale."
        text1={{ icon: Users01, message: "See who's sampled, posted and selling in each collab." }}
        text2={{ icon: TrendUp01, message: "Know which creators are driving your sales." }}
        text3={{ icon: Folder, message: "Group similar collabs together so they're easier to manage." }}
        image="/heroes/target-collabs.png"
      />
      <div className="flex flex-row items-center justify-end flex-shrink-0">
        <div className="flex items-center flex-row gap-2">
          <span className="text-sm font-medium text-tertiary mr-2">
            {isLoadingTarget ? "Loading..." : `${cachedTargetCount ?? 0} results`}
          </span>
          <Input
            size="sm"
            inputClassName="text-sm"
            className="w-64"
            value={search}
            onChange={setSearch}
            placeholder="Search collaboration name"
            icon={SearchMd}
          />
        </div>
      </div>

      {Array.from(expandedGroups).map((groupId) => (
        <GroupExpansionController key={groupId} groupId={groupId} onChange={updateGroupExpansion} />
      ))}
      <TargetCollaborationsTable
        data={targetData?.collaborations}
        metrics={targetMetrics}
        isLoading={isLoadingTarget}
        sortDescriptor={descriptor}
        onSortChange={setFromDescriptor}
        onReset={resetFilters}
        expandedGroups={expandedGroups}
        onToggleGroup={handleToggleGroup}
        groupExpansions={groupExpansions}
        hasFilters={Boolean(debouncedSearch)}
      />

      <PaginationPageMinimalCenter
        page={pagination.page}
        total={Math.ceil((targetData?.total ?? 0) / pagination.pagination.limit)}
        onPageChange={pagination.setPage}
        className="px-4 py-3 md:px-6 md:pt-2 md:pb-2 bg-primary"
      />

      <FilterPanel open={filtersOpen} close={() => setFiltersOpen(false)} clearAll={() => resetFilters()} />
      <CreateTargetCollaborationModal open={createModalOpen} onOpenChange={setCreateModalOpen} />
    </PageLayout>
  );
};

export const TargetCollaborationsPage = () => {
  return (
    <FiltersProvider activeFields={[]}>
      <TargetCollaborationsPageContent />
    </FiltersProvider>
  );
};
